import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["sourceId", "onInstallComplete", "onInstallError"];
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useMemo, useRef, useState } from 'react';
import ToolAccessTooltip from 'ui-tool-access/ToolAccessTooltip';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { useInstantiateTemplateMutation, useListInstantiationRequestsQuery } from '../../__generated__/chirp/com/hubspot/automation/templatesrpc/TemplateInstantiationService.hooks';
import { useGetActiveToolAccessRequestForScopeQuery } from '../../__generated__/chirp/com/hubspot/tool/access/rpc/ToolAccessService.hooks';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { getWorkflowFlowUrl, navigateTo } from '../utils/DetailsPageUrls';
import { useGetInstallability } from './hooks/useGetInstallability';
import { gate } from 'hub-http/gates';
import { jsx as _jsx } from "react/jsx-runtime";
const MAX_POLLING_TIME = 40000;
const WORKFLOWS_ACCESS_SCOPE_NAME = 'workflows-app-access';
export function TemplateCTAButton(_ref) {
  var _userInfo$gates;
  let {
      sourceId,
      onInstallComplete,
      onInstallError
    } = _ref,
    buttonProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    appContext,
    listingId,
    published,
    isEmbedded,
    productType,
    trackInteraction,
    trackInstall,
    userInfo
  } = useDetailsPageContext();
  const isUngatedForUnifiedBuilder = userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate('Workflows:ModernEditor'));
  const [pollingForCurrentTask, setPollingForCurrentTask] = useState(false);
  const maxPollingTimeReached = useRef(false);
  const taskId = useRef(undefined);
  const {
    data: workflowsAppAccessRequestData,
    refetch: refetchWorkflowsAppAccessRequest
  } = useGetActiveToolAccessRequestForScopeQuery({
    variables: {
      scopeName: WORKFLOWS_ACCESS_SCOPE_NAME
    },
    skip: appContext === 'admin-preview'
  });
  const onToolAccessRequestComplete = useCallback(() => {
    trackInteraction({
      action: 'workflows app access request sent'
    });
    void refetchWorkflowsAppAccessRequest().catch(error => {
      console.error('Error refetching workflows app access request status:', error);
    });
  }, [refetchWorkflowsAppAccessRequest, trackInteraction]);
  const {
    loading: installabilityLoading,
    portalInstallable,
    userInstallable,
    uninstallableReason,
    missingPlanTooltip
  } = useGetInstallability({
    listingId,
    appContext,
    published,
    productType
  });
  const needWorkflowAccessScope = useMemo(() => {
    var _uninstallableReason$;
    return portalInstallable && (uninstallableReason === null || uninstallableReason === void 0 || (_uninstallableReason$ = uninstallableReason.missingUserScopes) === null || _uninstallableReason$ === void 0 ? void 0 : _uninstallableReason$.includes(WORKFLOWS_ACCESS_SCOPE_NAME));
  }, [portalInstallable, uninstallableReason]);
  const sourceIdAsNumber = typeof sourceId === 'number' ? sourceId : parseInt(sourceId, 10);
  const [instantiateTemplate, {
    loading: instantiateTemplateLoading
  }] = useInstantiateTemplateMutation();
  const {
    refetch: refetchTemplateInstances
  } = useListInstantiationRequestsQuery({
    variables: {
      templateId: sourceIdAsNumber
    },
    skip: appContext !== 'app'
  });
  function showGenericErrorAlert() {
    FloatingAlertStore.addAlert({
      type: 'danger',
      maxInlineSize: 'unset',
      sticky: true,
      titleText: I18n.text('detailsPage.productDetailsCard.alerts.templates.templateInstantiationFailedAlertTitle'),
      message: I18n.text('detailsPage.productDetailsCard.alerts.templates.templateInstantiationFailedAlertMessage')
    });
    if (onInstallError) {
      onInstallError();
    }
  }
  function pollForCompletedTask() {
    setPollingForCurrentTask(true);
    refetchTemplateInstances().then(response => {
      var _response$data;
      const currentTask = response === null || response === void 0 || (_response$data = response.data) === null || _response$data === void 0 || (_response$data = _response$data.result) === null || _response$data === void 0 || (_response$data = _response$data.results) === null || _response$data === void 0 ? void 0 : _response$data.findLast(task => task.taskId === taskId.current);
      if (currentTask && currentTask.status === 'COMPLETED') {
        if (currentTask.flowId) {
          trackInteraction({
            action: 'created workflow from template',
            sourceId: [`WORKFLOW_TEMPLATE__${sourceIdAsNumber}`],
            value: String(currentTask.flowId)
          });
          navigateTo(getWorkflowFlowUrl(currentTask.flowId, isUngatedForUnifiedBuilder, currentTask.targetType), isEmbedded);
          if (onInstallComplete) {
            onInstallComplete();
          }
        } else {
          console.error('Missing flow Id in completed task response');
          showGenericErrorAlert();
        }
        setPollingForCurrentTask(false);
      } else if (maxPollingTimeReached.current) {
        showGenericErrorAlert();
        setPollingForCurrentTask(false);
      } else {
        setTimeout(() => {
          pollForCompletedTask();
        }, 2000);
      }
    }).catch(e => console.error(e));
  }
  function handleTemplateInstall() {
    trackInstall({});
    instantiateTemplate({
      variables: {
        targetType: 'HYBRID_FLOW',
        templateId: sourceIdAsNumber
      }
    }).then(response => {
      var _response$data2;
      const createdTaskId = (_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.result.taskId;
      if (createdTaskId) {
        taskId.current = createdTaskId;
        FloatingAlertStore.addAlert({
          type: 'success',
          maxInlineSize: 'unset',
          sticky: true,
          titleText: I18n.text('detailsPage.productDetailsCard.alerts.templates.templateInstantiationSuccessAlertTitle'),
          message: I18n.text('detailsPage.productDetailsCard.alerts.templates.templateInstantiationSuccessAlertMessage')
        });
      }
      setTimeout(() => {
        maxPollingTimeReached.current = true;
      }, MAX_POLLING_TIME);
      pollForCompletedTask();
    }).catch(e => {
      console.error(e);
      showGenericErrorAlert();
    });
  }
  if (installabilityLoading || instantiateTemplateLoading || pollingForCurrentTask) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({
      size: "medium",
      use: "primary",
      role: "button"
    }, buttonProps, {
      loading: true,
      disabled: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.WORKFLOW_TEMPLATE"
      })
    }));
  }
  const uninstallableReasonTooltip = missingPlanTooltip || I18n.text('detailsPage.productDetailsCard.cta.uninstallableReasons.cannotUseTemplate');
  if (needWorkflowAccessScope) {
    return /*#__PURE__*/_jsx(ToolAccessTooltip, {
      scope: WORKFLOWS_ACCESS_SCOPE_NAME,
      onComplete: onToolAccessRequestComplete,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        "data-test-id": "disabled-install-button-with-tool-access-tooltip"
      }, buttonProps, {
        disabled: true,
        children: (workflowsAppAccessRequestData === null || workflowsAppAccessRequestData === void 0 ? void 0 : workflowsAppAccessRequestData.status) === 'ACTIVE' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.toolAccessRequestPending"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.WORKFLOW_TEMPLATE"
        })
      }))
    });
  }
  if (!portalInstallable || !userInstallable) {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: uninstallableReasonTooltip,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary"
      }, buttonProps, {
        disabled: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.WORKFLOW_TEMPLATE"
        })
      }))
    });
  }
  return /*#__PURE__*/_jsx(Button, Object.assign({
    use: "primary",
    onClick: handleTemplateInstall
  }, buttonProps, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.install.WORKFLOW_TEMPLATE"
    })
  }));
}